import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  Typography,
  DialogActions,
  Tooltip,
} from '@mui/material';
import SbButton from '@Components/Button';
import Title from '@Components/Title';
import FilterSearch from '@Components/Filter/Search';
import DateRangePicker from '@Components/DateRangePicker';
import FilterSelect from '@Components/Filter/Dropdown';
import { AppDispatch, RootStateType } from '@Store';
import { INVOICE_ROUTE_TYPE } from '@Constants/Shipment';
import { searchFilterList, TableActionContainer, initialDateRange, StatusList } from './constants';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useLocation, useNavigate } from 'react-router-dom';
import { FilterObj } from '../Invoice/Type';
import { InvoiceListQuery } from '@Reducers/Invoice/Type';
import ReusableTable, { Column } from './components/ReusableTable';
import API from '@Services/apiAxios';
import CloseIcon from '@mui/icons-material/Close';
import { Status } from '../Invoice/Status';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { getCarriers } from './api/api';
import PaymentPurchaseInvoiceModal from './components/Pay/PaymentPurchaseInvoiceModal';
import ExportBtn from '@Components/Button/Export';
import { getSystemList } from '@Reducers/Systems';
import { fetchPurchaseInvoiceDownload } from '@Reducers/PurchaseInvoiceDownloadSlice/purchaseInvoiceDownloadSlice';
import { getAllReadNotifications, getAllUnreadNotifications } from '@Reducers/Notifications/actions';
import { SuccessErrorModalOpen, SuccessErrorModalReset } from '@Reducers/SuccessErrorModal';
import { toggleBoolean } from '@Reducers/booleanSlice/booleanSlice';

interface CarrierOptions {
  value: string;
  display: string;
}

// Create a separate component for the action menu to properly use hooks
const RowActions = ({
  row,
  onCommentsOpen,
  onRefresh,
}: {
  row: any;
  onCommentsOpen: (comments: any[]) => void;
  onRefresh: () => void;
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [openPayModal, setOpenPayModal] = useState(false);

  const openMenu = (event: React.MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget);
  const closeMenu = () => setMenuAnchor(null);

  const handlePay = () => {
    closeMenu();
    setOpenPayModal(true);
  };

  const handleDownload = () => {
    closeMenu();
    // existing Download logic (replace this with your API/download handler)
  };

  const handleComments = () => {
    closeMenu();
    onCommentsOpen(row.raw.comments);
  };

  const handleDelete = () => {
    closeMenu();
    // existing Delete logic (if any)
  };

  return (
    <>
      <IconButton onClick={openMenu} size="small">
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        PaperProps={{
          sx: { minWidth: 160 },
        }}
      >
        <MenuItem onClick={handlePay}>Pay</MenuItem>
        <Tooltip title={!row.customBilledCharges ? 'Invoice is not built from excel' : ''} placement="right">
          <span>
            <MenuItem onClick={handleDownload} disabled={!row.customBilledCharges}>
              Download Discrepancy Report
            </MenuItem>
          </span>
        </Tooltip>

        <MenuItem onClick={handleComments}>Comments</MenuItem>
        <Tooltip title={row.status === 'paid' ? "A paid invoice can't be voided." : ''} placement="right">
          <span>
            <MenuItem
              onClick={handleDelete}
              disabled={row.status === 'paid'}
              sx={{ pointerEvents: row.status === 'paid' ? 'none' : 'auto' }}
            >
              Void
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>
      <PaymentPurchaseInvoiceModal
        row={row}
        handleSuccess={() => {
          onRefresh();
          setOpenPayModal(false);
        }}
        isAccountHolder={true}
        open={openPayModal}
        onClose={() => setOpenPayModal(false)}
      />
    </>
  );
};

const PurchaseInvoiceList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [anchorElCreate, setAnchorElCreate] = useState<null | HTMLElement>(null);
  const location = useLocation();
  const purchaseInvoiceNumber = location.state?.purchaseInvoiceNumber;

  // Filters
  const [filterSelectValue, setFilterSelectValue] = useState('invoiceNumber');
  const [filterText, setFilterText] = useState('');
  const [dateRange, setDateRange] = useState({ ...initialDateRange });
  const [system, setSystem] = useState<string[]>([]);
  const [invoiceType, setInvoiceType] = useState<string[]>([]);
  const [shipmentType, setShipmentType] = useState<string[]>([]);
  const [status, setStatus] = useState<string[]>([]);
  const [carriers, setCarriers] = useState<string[]>([]);
  const [isClear, setClear] = useState(false);
  console.log(isClear);

  // Table state
  const [rows, setRows] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [openCommentsModal, setOpenCommentsModal] = useState(false);
  const [selectedComments, setSelectedComments] = useState<any[]>([]);
  const [carrierOptions, setCarrierOptions] = useState<CarrierOptions[]>([]);
  const { limit } = useSelector((state: RootStateType) => state.notifications);

  useEffect(() => {
    if (purchaseInvoiceNumber) {
      setFilterText(purchaseInvoiceNumber);
    }
  }, [purchaseInvoiceNumber]);

  const handleOpenComments = (comments: any[]) => {
    setSelectedComments(comments || []);
    setOpenCommentsModal(true);
  };

  const handleCloseComments = () => {
    setOpenCommentsModal(false);
    setSelectedComments([]);
  };

  const SystemList = useSelector((state: RootStateType) =>
    state.System.data.selectedList.map(system => ({
      display: system.name,
      value: system._id,
    })),
  );
  const _systems = useSelector((state: RootStateType) => state.loginUser?.profile?._systems);

  const activeSystemList = useMemo(
    () => (_systems?.length ? SystemList.filter(s => _systems.includes(s.value)) : []),
    [SystemList, _systems],
  );

  const INVOICE_ROUTE_TYPE_DROPDOWN_LIST = useMemo(
    () =>
      Object.keys(INVOICE_ROUTE_TYPE).map(key => ({
        display: INVOICE_ROUTE_TYPE[key]?.label,
        value: INVOICE_ROUTE_TYPE[key]?.value,
      })),
    [],
  );

  // 🔹 Build query params from filters
  const getFilterObj = (options: FilterObj = {}) => {
    const filterDate = options.dateRange || dateRange;
    const filterSystem = options.system || system;
    const filterTypeInvoice = options.invoiceType || invoiceType;
    const filterTypeShipment = options.shipmentType || shipmentType;
    const filterStatus = options.status || status;
    const filterCarriers = options.carriers || carriers;

    const fromDate = moment(filterDate.startDate).format('DD/MM/YYYY');
    const toDate = moment(filterDate.endDate).format('DD/MM/YYYY');

    // const filterSearchKey = filterSelectValue;
    const filterSearchValue = 'searchValue' in options ? options.searchValue?.toUpperCase() : filterText;

    const filterQuery: InvoiceListQuery & { carriers?: string[] } = {
      currentPage: (options.currentPage ?? page) + 1,
      perPage: options.perPage || rowsPerPage,
      fromDate,
      toDate,
      systems: filterSystem,
      invoiceType: filterTypeInvoice,
      type: filterTypeShipment,
      status: filterStatus,
      searchKey: 'invoiceNumber',
      // searchKey: filterSearchKey,
      searchValue: filterSearchValue,
    };

    if (filterCarriers && filterCarriers.length > 0) {
      filterQuery.carriers = filterCarriers;
    }

    if (!filterSearchValue) {
      delete filterQuery.searchKey;
      delete filterQuery.searchValue;
    }

    return filterQuery;
  };

  // 🔹 API Call
  const fetchInvoices = async (options: FilterObj = {}) => {
    try {
      setLoading(true);
      const params = getFilterObj(options);

      const res = await API.get('/invoice/purchase-invoice/list', { params });
      const { invoices, totalCount } = res.data.data;
      const mapped = invoices.map((inv: any, index: number) => ({
        srNo: index + 1,
        carrier: inv.carrier,
        invoiceNo: inv.invoiceNumber,
        customBilledCharges: inv.customBilledCharges,
        carrierReferenceNumber: inv.carrierReferenceNumber || '-',
        refInvoiceNo: inv.refInvoiceno || '-',
        refDate: inv.refDate ? moment(inv.refDate).format('DD/MM/YYYY') : '-',
        pending: (inv.invoiceAmount - inv.paidAmount - inv.deductionPaid).toFixed(2),
        paidAmount: inv.paidAmount?.toFixed(2),
        deductionPaid: inv.deductionPaid?.toFixed(2),
        totalAmount: inv.invoiceAmount?.toFixed(2),
        invoiceDate: inv.billDate ? moment(inv.billDate).format('DD/MM/YYYY') : '-',
        system: inv.system || '-',
        service: inv.service || '-',
        dueDate: inv.dueDate ? moment(inv.dueDate).format('DD/MM/YYYY') : '-',
        status: inv.status,
        raw: inv,
      }));

      setRows(mapped);
      setTotalCount(totalCount);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Initial + on filter change
  useEffect(() => {
    fetchInvoices({ currentPage: page, perPage: rowsPerPage });
  }, [
    page,
    rowsPerPage,
    system,
    invoiceType,
    shipmentType,
    status,
    carriers,
    filterText,
    dateRange,
    purchaseInvoiceNumber,
  ]);

  const handleDateChange = useCallback(
    ({ startDate, endDate }: { startDate: Date; endDate: Date }, isOpen: boolean) => {
      if (isOpen) return;
      setDateRange({ startDate, endDate });
    },
    [],
  );

  const handleFilterChange = useCallback(
    (setter: React.Dispatch<React.SetStateAction<string[]>>) => (selected: string[]) => {
      setter(selected);
      setPage(0);
    },
    [],
  );

  const handleClearFilters = useCallback(() => {
    setSystem([]);
    setInvoiceType([]);
    setShipmentType([]);
    setStatus([]);
    setCarriers([]);
    setFilterText('');
    setDateRange(initialDateRange);
    setClear(true);
    setPage(0);
  }, []);

  const handleCreateMenuClick = (event: React.MouseEvent<HTMLElement>) => setAnchorElCreate(event.currentTarget);

  const handleCreateMenuClose = (type: 'create' | 'credit' | 'debit' | null) => {
    setAnchorElCreate(null);
    if (type === 'create') navigate('/purchase-invoices/creation');
    if (type === 'credit') navigate('/purchase-invoices/credit-creation');
    if (type === 'debit') navigate('/purchase-invoices/debit-creation');
  };

  const columns: Column[] = [
    { id: 'srNo', label: 'Sr No' },
    {
      id: 'carrier',
      label: 'Carrier Details',
      render: row => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '20px' }}>
          <span>{row.carrier?.name || '-'}</span>
          <span style={{ fontSize: '14px', color: '#666' }}>{row.carrierReferenceNumber || '-'}</span>
        </div>
      ),
    },
    { id: 'invoiceNo', label: 'Invoice No' },
    { id: 'refInvoiceNo', label: 'Ref. Invoice No' },
    { id: 'paidAmount', label: 'Paid Amount' },
    { id: 'deductionPaid', label: 'Deduction Paid' },
    { id: 'pending', label: 'Pending Amount' },
    { id: 'totalAmount', label: 'Total Amount' },
    { id: 'invoiceDate', label: 'Invoice Date' },
    { id: 'dueDate', label: 'Due Date' },
    {
      id: 'status',
      label: 'Status',
      render: row => (
        <span style={{ color: row.status === 'unpaid' ? 'red' : 'green' }}>
          <Status status={row.status} />
        </span>
      ),
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getCarriers();
        const mappedCarriersOptions = data?.map((c: { _id: string; name: string; code: string }) => ({
          value: c._id,
          display: c.name,
        }));
        setCarrierOptions(mappedCarriersOptions);
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
    dispatch(getSystemList());
  }, []);

  const refreshTable = () => {
    fetchInvoices({ currentPage: page, perPage: rowsPerPage });
  };

  return (
    <>
      <Title title="Purchase Invoices" subTitle="View and create purchase invoices." removeBorder />

      {/* Action Buttons */}
      <Box className="action-buttons" sx={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <SbButton
          variant="contained"
          onClick={handleCreateMenuClick}
          endIcon={<ArrowDropDownIcon />}
          sx={{ borderRadius: '8px !important', minWidth: '225px' }}
        >
          Create New
        </SbButton>
        <Menu anchorEl={anchorElCreate} open={Boolean(anchorElCreate)} onClose={() => handleCreateMenuClose(null)}>
          <MenuItem onClick={() => handleCreateMenuClose('create')}>Invoice</MenuItem>
          <MenuItem onClick={() => handleCreateMenuClose('credit')}>Credit Note</MenuItem>
          <MenuItem onClick={() => handleCreateMenuClose('debit')}>Debit Note</MenuItem>
        </Menu>

        <ExportBtn
          iconSrc="/images/file_download.svg"
          width={25}
          alt="Export"
          label="Export"
          onClick={() => {
            const filters = getFilterObj();
            dispatch(
              fetchPurchaseInvoiceDownload({
                ...filters,
                currentPage: page,
                perPage: rowsPerPage,
                fromDate: filters.fromDate!,
                toDate: filters.toDate!,
              }),
            );
            dispatch(
              getAllUnreadNotifications({
                page: 1,
                limit,
              }),
            );
            dispatch(getAllReadNotifications({ page: 1, limit }));
            dispatch(
              SuccessErrorModalOpen({
                type: 'success',
                title: 'Download in Progress',
                subTitle: 'Your report is being generated. It will be available shortly in the downloads bar',
                btnText: 'Close',
                onConfirm: () => {
                  dispatch(toggleBoolean());
                  dispatch(SuccessErrorModalReset());
                },
              }),
            );
          }}
        />
      </Box>

      {/* Filters */}
      <TableActionContainer>
        <FilterSearch
          onSelectChange={setFilterSelectValue}
          list={searchFilterList}
          selectValue={filterSelectValue}
          textValue={filterText}
          onTextChange={e => setFilterText(e.target.value)}
          onTextSearch={() => setPage(0)}
          onTextClear={() => {
            setFilterText('');
            setPage(0);
          }}
        />
        <DateRangePicker onChange={handleDateChange} value={dateRange} />
        <FilterSelect
          id="system"
          label="System"
          options={activeSystemList}
          value={system}
          onChange={handleFilterChange(setSystem)}
        />
        <FilterSelect
          id="type"
          label="Invoice Type"
          options={INVOICE_ROUTE_TYPE_DROPDOWN_LIST}
          value={invoiceType}
          onChange={handleFilterChange(setInvoiceType)}
        />
        <FilterSelect
          id="status"
          label="Status"
          options={StatusList}
          value={status}
          onChange={handleFilterChange(setStatus)}
        />
        <FilterSelect
          id="carriers"
          label="Carriers"
          value={carriers}
          options={carrierOptions}
          onChange={handleFilterChange(setCarriers)}
        />
        <SbButton
          className="xs-text ml-1"
          variant="outlined"
          sx={{ borderRadius: '8px !important', padding: '8px !important' }}
          onClick={handleClearFilters}
        >
          Clear Filters
        </SbButton>
      </TableActionContainer>

      {/* Table */}
      <ReusableTable
        columns={columns}
        rows={rows}
        loading={loading}
        pagination
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={e => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        actions={row => <RowActions row={row} onCommentsOpen={handleOpenComments} onRefresh={refreshTable} />}
      />

      <Dialog open={openCommentsModal} onClose={handleCloseComments} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Comments
          <IconButton onClick={handleCloseComments} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedComments.length > 0 ? (
            <Stack spacing={2}>
              {selectedComments.map((comment, index) => (
                <Box
                  key={index}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    p: 1.5,
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {comment.user?.name || 'Unknown User'}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {comment?.deduction
                      ? `Paid by Deduction: ${comment?.type === 'credit' ? '- ' : ''}${comment.paidAmount}`
                      : `Paid: ${comment?.type === 'credit' ? '- ' : ''}${comment.paidAmount || 'N/A'}`}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {comment.comment}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {moment(comment.createdAt).format('DD MMM YYYY, HH:mm')}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" align="center">
              No comments available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseComments} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PurchaseInvoiceList;
